import React, { useState, useEffect } from 'react';
import { staffAPI } from '../services/api';
import StaffsIcon from '../img/staffs.gif';
import { PencilSquare, Trash, Search, Eye, EyeSlash,
         ExclamationTriangleFill, CheckCircleFill, InfoCircleFill } from 'react-bootstrap-icons';
import StaffsGuideModal from '../Components/StaffsGuideModal.jsx';
import '../CSS/Staffs.css';

function Staffs() {
  const [showPassword, setShowPassword]   = useState(false);
  const [staffs, setStaffs]               = useState([]);
  const [isLoading, setIsLoading]         = useState(false);
  const [newStaff, setNewStaff]           = useState({
    name: '', position: '', email: '', phone: '', password: '',
  });
  const [editMode, setEditMode]           = useState(false);
  const [editId, setEditId]               = useState(null);
  const [search, setSearch]               = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId]           = useState(null);
  const [toast, setToast]                 = useState({ show: false, message: '', variant: '' });

  useEffect(() => {
    fetchStaffs();
  }, []);

  // Auto-dismiss toast after 3 s
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    return () => clearTimeout(t);
  }, [toast.show]);

  const fetchStaffs = () => {
    setIsLoading(true);
    staffAPI.getStaffs()
      .then(response => setStaffs(response.data))
      .catch(error => console.error('There was an error fetching the data!', error))
      .finally(() => setIsLoading(false));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewStaff({ ...newStaff, [name]: value });
  };

  const validateEmail    = (email)    => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone    = (phone)    => /^[0-9]{8,}$/.test(phone);
  const validatePosition = (position) => /^[A-Za-z\s]+$/.test(position);
  const validateName     = (name) => {
    if (!/^[A-Za-z\s]+$/.test(name)) return false;
    return name.trim().split(/\s+/).length >= 2;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const { name, position, email, phone, password } = newStaff;

    if (!name || !position || !email || !phone || !password) {
      setToast({ show: true, message: 'Please fill in all fields.', variant: 'danger' });
      return;
    }
    if (!validateName(name)) {
      setToast({ show: true, message: 'Please enter a full name (first name and surname).', variant: 'danger' });
      return;
    }
    if (!validatePosition(position)) {
      setToast({ show: true, message: 'Position should contain only letters and spaces.', variant: 'danger' });
      return;
    }
    if (!validateEmail(email)) {
      setToast({ show: true, message: 'Please enter a valid email address.', variant: 'danger' });
      return;
    }
    if (!validatePhone(phone)) {
      setToast({ show: true, message: 'Phone number should contain only numbers and be at least 8 digits long.', variant: 'danger' });
      return;
    }
    if (password.length < 8) {
      setToast({ show: true, message: 'Password should be at least 8 characters long.', variant: 'danger' });
      return;
    }

    if (editMode) {
      staffAPI.updateStaff(editId, newStaff)
        .then(() => {
          setStaffs(staffs.map(s => (s.id === editId ? { ...newStaff, id: editId } : s)));
          setEditMode(false);
          setEditId(null);
          setNewStaff({ name: '', position: '', email: '', phone: '', password: '' });
          setToast({ show: true, message: 'Staff Information Updated Successfully', variant: 'success' });
        })
        .catch(error => console.error('There was an error updating the staff member!', error));
    } else {
      staffAPI.addStaff(newStaff)
        .then(response => {
          setStaffs([...staffs, { ...newStaff, id: response.data.insertId }]);
          setNewStaff({ name: '', position: '', email: '', phone: '', password: '' });
          setToast({ show: true, message: 'Staff Added Successfully', variant: 'success' });
        })
        .catch(error => {
          if (error.response && error.response.status === 409) {
            setToast({ show: true, message: 'The Email is being used by someone else or the Name is already taken', variant: 'danger' });
          } else {
            console.error('There was an error adding the staff member!', error);
          }
        });
    }
  };

  const handleDeleteConfirmation = (id) => { setDeleteId(id); setShowDeleteModal(true); };
  const handleDeleteCancel       = ()   => { setDeleteId(null); setShowDeleteModal(false); };

  const handleDeleteConfirm = () => {
    staffAPI.deleteStaff(deleteId)
      .then(() => {
        setStaffs(staffs.filter(s => s.id !== deleteId));
        setShowDeleteModal(false);
        setToast({ show: true, message: 'Staff Deleted Successfully', variant: 'success' });
      })
      .catch(error => console.error('There was an error deleting the staff member!', error));
  };

  const handleStaffEdit = (staff) => {
    setEditMode(true);
    setEditId(staff.id);
    setNewStaff({ ...staff });
  };

  const filteredStaffs = staffs.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="stf-page">

      {/* ── Toast ── */}
      <div className="stf-toast-region" aria-live="polite" aria-atomic="true">
        <div className={`stf-toast stf-toast-${toast.variant} ${toast.show ? 'stf-toast-show' : ''}`} role="alert">
          <div className="stf-toast-header">
            <span className="stf-toast-title">
              {toast.variant === 'success' ? 'Success' : 'Error'}
            </span>
            <button className="stf-toast-close" onClick={() => setToast(p => ({ ...p, show: false }))} aria-label="Close">×</button>
          </div>
          <div className="stf-toast-body">
            {toast.variant === 'success'
              ? <CheckCircleFill className="stf-toast-icon" />
              : <ExclamationTriangleFill className="stf-toast-icon" />}
            {toast.message}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="stf-inner-container">

        {/* Page heading */}
        <div className="row my-4">
          <div className="col-12">
            <h3>
              Staffs
              <img className="stf-animated-icon" src={StaffsIcon} alt="Staffs icon" />
            </h3>
          </div>
        </div>

        {/* Search */}
        <div className="stf-search-wrapper">
          <Search className="stf-search-icon" />
          <input
            type="text"
            className="stf-search-input"
            placeholder="Search Staffs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Two-column layout */}
        <div className="stf-layout">

          {/* ── Table column ── */}
          <div className="stf-table-col">
            <h4 className="stf-section-label">Staff Members</h4>
            <div className="stf-table-container">
              <div className="stf-table-wrapper">
                <table className="stf-table">
                  <thead>
                    <tr className="stf-table-header">
                      <th>Name</th>
                      <th>Position</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="stf-table-body">
                    {isLoading ? (
                      <tr>
                        <td colSpan="5" className="stf-loading-state">
                          <div className="stf-loader-wrap">
                            <div className="stf-loader-ring" />
                            <span className="stf-loader-text">Loading Staff Members</span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredStaffs.length > 0 ? (
                      filteredStaffs.map(staff => (
                        <tr key={staff.id}>
                          <td className="stf-cell">{staff.name}</td>
                          <td className="stf-cell">{staff.position}</td>
                          <td className="stf-cell">{staff.email}</td>
                          <td className="stf-cell">{staff.phone}</td>
                          <td className="stf-cell">
                            <span className="stf-cell-actions">
                              <button
                                className="stf-action-btn stf-action-edit"
                                onClick={() => handleStaffEdit(staff)}
                                title="Edit staff"
                              >
                                <PencilSquare />
                              </button>
                              <button
                                className="stf-action-btn stf-action-delete"
                                onClick={() => handleDeleteConfirmation(staff.id)}
                                title="Delete staff"
                              >
                                <Trash />
                              </button>
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="stf-empty-state">
                          <InfoCircleFill className="stf-empty-icon" />
                          Staff not found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Form column ── */}
          <div className="stf-form-col">
            <h4 className="stf-section-label">{editMode ? 'Edit Staff' : 'Add Staff'}</h4>
            <div className="stf-form-card">
              <form className="stf-form" onSubmit={handleFormSubmit}>

                <div className="stf-form-field">
                  <input
                    className="stf-form-input"
                    type="text"
                    name="name"
                    placeholder=" "
                    value={newStaff.name}
                    onChange={handleFormChange}
                  />
                  <label className="stf-form-label">Name</label>
                </div>

                <div className="stf-form-field">
                  <input
                    className="stf-form-input"
                    type="text"
                    name="position"
                    placeholder=" "
                    value={newStaff.position}
                    onChange={handleFormChange}
                  />
                  <label className="stf-form-label">Position</label>
                </div>

                <div className="stf-form-field">
                  <input
                    className="stf-form-input"
                    type="email"
                    name="email"
                    placeholder=" "
                    value={newStaff.email}
                    onChange={handleFormChange}
                  />
                  <label className="stf-form-label">Email</label>
                </div>

                <div className="stf-form-field">
                  <input
                    className="stf-form-input"
                    type="text"
                    name="phone"
                    placeholder=" "
                    value={newStaff.phone}
                    onChange={handleFormChange}
                  />
                  <label className="stf-form-label">Phone</label>
                </div>

                <div className="stf-form-field">
                  <input
                    className="stf-form-input stf-form-input--password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder=" "
                    value={newStaff.password}
                    onChange={handleFormChange}
                  />
                  <label className="stf-form-label">Password</label>
                  <button
                    type="button"
                    className="stf-password-toggle"
                    onClick={() => setShowPassword(p => !p)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeSlash /> : <Eye />}
                  </button>
                </div>

                <button type="submit" className="stf-btn stf-btn-success">
                  {editMode ? 'Save Changes' : 'Save'}
                </button>

                {editMode && (
                  <button
                    type="button"
                    className="stf-btn stf-btn-secondary"
                    onClick={() => {
                      setEditMode(false);
                      setEditId(null);
                      setNewStaff({ name: '', position: '', email: '', phone: '', password: '' });
                    }}
                  >
                    Cancel
                  </button>
                )}
              </form>
            </div>
          </div>

        </div>{/* end stf-layout */}

        {/* ── Delete Confirmation Modal ── */}
        {showDeleteModal && (
          <div className="stf-modal-overlay" onClick={handleDeleteCancel}>
            <div className="stf-modal-content" onClick={e => e.stopPropagation()}>
              <div className="stf-modal-header stf-modal-header--danger">
                <h5 className="stf-modal-title">Confirm Delete</h5>
                <button className="stf-modal-close" onClick={handleDeleteCancel} aria-label="Close">×</button>
              </div>
              <div className="stf-modal-body">
                <div className="stf-delete-alert">
                  Are you sure you want to delete this staff member?
                </div>
              </div>
              <div className="stf-modal-footer stf-modal-footer--dark">
                <button className="stf-modal-btn stf-modal-btn--secondary" onClick={handleDeleteCancel}>
                  Cancel
                </button>
                <button className="stf-modal-btn stf-modal-btn--danger" onClick={handleDeleteConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
          <StaffsGuideModal />
    </div>
  );
}

export default Staffs;